import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { captureError } from '@clatter/platform';
import { buttonVariants } from '@clatter/ui';
import { addNotification } from '../../../store';
import { setDocumentsButtonsEnabled } from '../../../store/UI/projectStepper.slice';

import { useAssetDownload, useUpload } from '../../../hooks';
import { NgFileUpload } from '../../NgFileUpload/NgFileUpload';
import { IconButtonRounded } from '../../UI';
import { IngestError, IngestStatus } from './components/IngestStatus';
import { useCreateIngestJobMutation } from '../../../services/ingest.service';

interface WorkbookUploadProps {
  workbookName?: string;
  targetId?: number;
  targetKey?: string;
  uploadFolderId: number;
  project?: any;
  currentAsset?: FolderManagerItem | null;
  onComplete: (data: IngestJob) => void;
  onError?: (data: any) => void;
}

const StyledContainer = styled.div`
  min-height: 240px;
  min-width: 240px;
  display: grid;
  grid-template-rows: 1fr;
`;

const StyledFileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.small} 0;

  a {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;

    &:hover {
      text-decoration: underline;
    }
  }

  .actions {
    display: flex;
    gap: ${({ theme }) => theme.spacing.small};
    flex-shrink: 0;
  }
`;

const StyledReplaceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.small};

  span {
    font-size: ${({ theme }) => theme.fontSize.sm};
    font-weight: 600;
  }
`;

const StyledReadyContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.medium};
  border: 1px solid ${({ theme }) => theme.palette.cardBorder};
  border-radius: ${({ theme }) => theme.radius.small};
`;

export const WorkbookUpload: FC<WorkbookUploadProps> = ({
  project,
  targetKey = 'project_id',
  targetId,
  uploadFolderId,
  currentAsset,
  onComplete,
  onError,
  workbookName,
}) => {
  const dispatch = useDispatch();
  const { uploadFiles } = useUpload();
  const { downloadAssetById } = useAssetDownload();

  const [ingestId, setIngestId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [ingestMessage, setIngestMessage] = useState<string | null>(null);
  const [showReplace, setShowReplace] = useState(false);

  const [createIngestJob] = useCreateIngestJobMutation();

  const handleIngestStatusComplete = async (payload: IngestJob) => {
    dispatch(
      addNotification({
        type: 'success',
        message: payload?.user_status || 'Workbook has been successfully parsed.',
      }),
    );

    dispatch(setDocumentsButtonsEnabled(true));

    setIngestId(null);
    setShowReplace(false);
    onComplete(payload);
    setIngestMessage(null);
  };

  const handleIngestError = (data: IngestError) => {
    dispatch(
      addNotification({
        type: 'error',
        message: data.error,
      }),
    );

    setIngestId(null);
    setIngestMessage(null);
  };

  const handleAssetUpload = async (data: any) => {
    setIsUploading(true);

    setIngestMessage('Uploading workbook.');

    const uploadResponse = await uploadFiles({ file: data, folderId: uploadFolderId });

    if (uploadResponse?.error || uploadResponse?.status === 'failed') {
      return;
    }

    const bucket = uploadResponse?.responseData?.Bucket;
    const key = uploadResponse?.responseData?.Key;

    if (!key || !bucket) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Upload failed — the server didn\'t respond as expected. Please try again.',
        }),
      );
      captureError(new Error(`Failed to create job - upload response incomplete. Invalid upload response: missing ${
          !bucket ? 'bucket' : ''
        } ${!key ? 'key' : ''}`.trim()), { component: 'WorkbookUpload', skipConsole: true });
      return;
    }

    setIngestMessage('Creating new job.');

    let ingestResponse;

    ingestResponse = await createIngestJob({
      key: key,
      file_name: key,
      bucket: bucket,
      [targetKey]: project?.ID,
    });

    //@ts-ignore
    const { ID } = ingestResponse?.data?.data;

    if (!ID) {
      const message = 'Could not start polling. No ingest job ID!';
      dispatch(
        addNotification({
          type: 'error',
          message: message,
        }),
      );
      captureError(new Error(message), { component: 'WorkbookUpload', skipConsole: true });
    }

    setIngestId(ID);
    setIsUploading(false);
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentAsset?.id) {
      downloadAssetById({ assetId: currentAsset.id });
    }
  };

  if (ingestId || ingestMessage) {
    return (
      <StyledReadyContainer>
        <IngestStatus
          ingestId={ingestId}
          message={ingestMessage}
          onComplete={handleIngestStatusComplete}
          onError={handleIngestError}
        />
      </StyledReadyContainer>
    );
  }

  if (currentAsset) {
    return (
      <StyledReadyContainer>
        <StyledFileRow>
          <a href="#" onClick={handleDownload}>
            {currentAsset.displayName || currentAsset.name}
          </a>
          <div className="actions">
            <IconButtonRounded
              title="Download"
              icon={<CloudDownloadOutlinedIcon />}
              filled
              materialIcon
              onClick={handleDownload}
            />
            {!showReplace && (
              <IconButtonRounded
                title="Replace workbook"
                icon={<FileUploadOutlinedIcon />}
                filled
                materialIcon
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setShowReplace(true);
                }}
              />
            )}
          </div>
        </StyledFileRow>

        {showReplace && (
          <>
            <StyledReplaceHeader>
              <span>Replace workbook</span>
              <IconButtonRounded
                title="Cancel"
                icon={<CloseIcon />}
                materialIcon
                onClick={() => setShowReplace(false)}
              />
            </StyledReplaceHeader>
            <NgFileUpload
              title={workbookName}
              targetKey={targetKey}
              targetId={targetId}
              disabled={isUploading}
              buttonVariant={buttonVariants.clear}
              onUpload={handleAssetUpload}
              onError={onError}
            />
          </>
        )}
      </StyledReadyContainer>
    );
  }

  return (
    <StyledContainer>
      <NgFileUpload
        title={workbookName}
        targetKey={targetKey}
        targetId={targetId}
        disabled={isUploading}
        onUpload={handleAssetUpload}
        onError={onError}
      />
    </StyledContainer>
  );
};
